import { createColumnHelper } from "@tanstack/react-table";
import {
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/solid";
import { formatDate } from "../../../utils/formatNumber";

const columnHelper = createColumnHelper();

export const orderTableColumnsConfig = (handleNavigateToDetail) => [
  columnHelper.accessor("order_id", {
    header: "Order ID",
    cell: (info) => info.getValue(),
  }),

  columnHelper.accessor("num_of_items", {
    header: "Qty",
    cell: (info) => info.getValue(),
  }),

  columnHelper.accessor("total_amount", {
    header: "Amount",
    cell: (info) => info.getValue(),
  }),

  columnHelper.accessor("postal_code", {
    header: "PinCode",
    cell: (info) => info.getValue(),
  }),

  // ---------------- PAYMENT STATUS ----------------
  columnHelper.accessor("payment_status", {
    header: "Payment Status",
    cell: ({ getValue }) => {
      const status = getValue()?.toLowerCase();

      const config = {
        pending: {
          label: "Pending",
          bg: "#FFF4E5",
          color: "#FF9500",
          Icon: ClockIcon,
        },
        paid: {
          label: "Paid",
          bg: "#E6F7EF",
          color: "#00A167",
          Icon: CheckCircleIcon,
        },
        cancelled: {
          label: "Cancelled",
          bg: "#FDECEA",
          color: "#D92D20",
          Icon: XCircleIcon,
        },
        failed: {
          label: "Failed",
          bg: "#FDECEA",
          color: "#D92D20",
          Icon: XCircleIcon,
        },
      }[status];

      if (!config) return "-";

      return <StatusPill {...config} />;
    },
  }),

  // ---------------- ORDER STATUS ----------------
  columnHelper.accessor("order_status", {
    header: "Order Status",
    cell: ({ getValue }) => {
      const status = getValue()?.toLowerCase().trim();

      const config = {
        pending: {
          label: "Pending",
          bg: "#FFF4E5",
          color: "#FF9500",
          Icon: ClockIcon,
        },
        "in process": {
          label: "In Process",
          bg: "#E8F0FE",
          color: "#2563EB",
          Icon: ArrowPathIcon,
        },
        "cancellation requested": {
          label: "Cancellation Requested",
          bg: "#FFF0F6",
          color: "#C21313",
          // border: "1px solid #C21313",
          Icon: ExclamationTriangleIcon,
        },
        cancelled: {
          label: "Cancelled",
          bg: "#FDECEA",
          color: "#D92D20",
          Icon: XCircleIcon,
        },
        "cancellation rejected": {
          label: "Cancellation Rejected",
          bg: "#F3E8FF",
          color: "#6D28D9",
          Icon: ShieldCheckIcon,
        },
        fulfilled: {
          label: "Fulfilled",
          bg: "#E6F7EF",
          color: "#00A167",
          Icon: CheckCircleIcon,
        },
        failed: {
          label: "Failed",
          bg: "#FDECEA",
          color: "#B42318",
          Icon: XCircleIcon,
        },
      }[status];

      if (!config) return "-";

      return <StatusPill {...config} />;
    },
  }),

  // ---------------- ORDERED ON ----------------
  columnHelper.accessor("ordered_on", {
    header: "Ordered On",
    cell: (info) => formatDate(info.getValue()),
  }),

  // ---------------- APPROVAL STATUS ----------------
  columnHelper.accessor("approval_status", {
    header: "Approval Status",
    cell: ({ getValue }) => {
      const status = getValue()?.toLowerCase();

      const config = {
        pending: {
          label: "Pending",
          bg: "#FFF4E5",
          color: "#FF9500",
          Icon: ClockIcon,
        },
        approved: {
          label: "Approved",
          bg: "#E6F7EF",
          color: "#00A167",
          Icon: CheckCircleIcon,
        },
        rejected: {
          label: "Rejected",
          bg: "#FDECEA",
          color: "#D92D20",
          Icon: XCircleIcon,
        },
      }[status];

      if (!config) return "-";

      return <StatusPill {...config} />;
    },
  }),

  // ---------------- ACTIONS ----------------
  columnHelper.display({
    id: "actions",
    header: <div style={{ textAlign: "center" }}>Action</div>,
    cell: (props) => (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <button
          style={actionBtnStyle}
          onClick={() => handleNavigateToDetail(props.row.original.id)}
        >
          View Details
        </button>
      </div>
    ),
  }),
];

// ---------------- REUSABLE COMPONENT ----------------
export const StatusPill = ({ label, bg, color, Icon, border }) => (
  <div
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      padding: "4px 10px",
      borderRadius: "999px",
      fontSize: "12px",
      fontWeight: 500,
      fontFamily: "Poppins, sans-serif",
      backgroundColor: bg,
      color,
      whiteSpace: "nowrap",
      border: border || "none",
    }}
  >
    <Icon style={{ width: 14, height: 14 }} />
    {label}
  </div>
);

// ---------------- BUTTON STYLE ----------------
const actionBtnStyle = {
  padding: "6px 12px",
  background: "linear-gradient(135deg, #2F3567 0%, #5561A0 100%)",
  color: "#FFFFFF",
  fontWeight: 500,
  fontSize: "13px",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
  transition: "all 0.3s ease",
  boxShadow: "0 2px 6px rgba(47,53,103,0.3)",
};
